import React, { useState, useEffect } from "react";
import {
  X,
  GraduationCap,
  Award,
  BookOpen,
  FileText,
  TrendingUp,
  Calendar,
  Hash,
  FileDown,
  Loader2,
  Edit3,
  Save,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import html2pdf from "html2pdf.js";
import { updateResult } from "../../../api/elearning/results/results";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";

const StudentTranscriptModal = ({ isOpen, student, onClose, onSaved }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    entrance_score: "",
    midterm_1: "",
    final_1: "",
    midterm_2: "",
    final_2: "",
    exit_score: "",
    remarks: "",
  });

  useEffect(() => {
    if (student) {
      setForm({
        entrance_score: student.entrance_score ?? "",
        midterm_1: student.midterm_1 ?? "",
        final_1: student.final_1 ?? "",
        midterm_2: student.midterm_2 ?? "",
        final_2: student.final_2 ?? "",
        exit_score: student.exit_score ?? "",
        remarks: student.remarks ?? "",
      });
      setIsEditing(false);
    }
  }, [student]);

  if (!student) return null;

  const getRankText = (avg) => {
    if (avg == null) return "—";
    if (avg >= 8) return "Giỏi";
    if (avg >= 6.5) return "Khá";
    if (avg >= 5) return "Trung bình";
    return "Yếu";
  };

  // ✅ Hàm xử lý xuất PDF
  const handleExportPDF = async () => {
    const element = document.getElementById("transcript-content");
    if (!element) return;

    setIsExporting(true); // Bắt đầu loading

    const opt = {
      margin: 10,
      filename: `Bang-Diem-${student.full_name.replace(/\s+/g, "-")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    try {
      // Chuyển sang dùng Promise để có thể await
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error("Lỗi xuất PDF:", error);
    } finally {
      setIsExporting(false); // Tắt loading sau khi hoàn tất
    }
  };

  const infoItem = (Icon, label, value, color = "text-gray-500") => (
    <div className="flex items-start gap-3 p-3 rounded-2xl hover:bg-gray-50 transition-colors text-left">
      <div
        className={`p-2 rounded-xl bg-white shadow-sm border border-gray-100 ${color}`}
      >
        <Icon size={16} />
      </div>
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
          {label}
        </p>
        <p className="text-sm font-bold text-gray-800 leading-tight">
          {value || "---"}
        </p>
      </div>
    </div>
  );

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        entrance_score: form.entrance_score === "" ? null : parseFloat(form.entrance_score),
        midterm_1: form.midterm_1 === "" ? null : parseFloat(form.midterm_1),
        final_1: form.final_1 === "" ? null : parseFloat(form.final_1),
        midterm_2: form.midterm_2 === "" ? null : parseFloat(form.midterm_2),
        final_2: form.final_2 === "" ? null : parseFloat(form.final_2),
        exit_score: form.exit_score === "" ? null : parseFloat(form.exit_score),
        remarks: form.remarks || null,
      };
      const res = await updateResult(student.id, payload);
      showSuccessToast("Đã cập nhật bảng điểm");
      onSaved?.({ ...student, ...res });
      setIsEditing(false);
      setForm({
        entrance_score: res.entrance_score ?? "",
        midterm_1: res.midterm_1 ?? "",
        final_1: res.final_1 ?? "",
        midterm_2: res.midterm_2 ?? "",
        final_2: res.final_2 ?? "",
        exit_score: res.exit_score ?? "",
        remarks: res.remarks ?? "",
      });
    } catch {
      showErrorToast("Không thể cập nhật bảng điểm");
    } finally {
      setSaving(false);
    }
  };

  const scoreBadge = (label, score, subLabel, fieldKey) => {
    const val = fieldKey && isEditing ? form[fieldKey] : score;
    return (
      <div className="bg-white p-4 rounded-3xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center">
        <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter mb-1">
          {label}
        </span>
        {isEditing && fieldKey ? (
          <input
            type="number"
            min="0"
            max="10"
            step="0.5"
            value={val}
            onChange={(e) => setForm({ ...form, [fieldKey]: e.target.value })}
            className="w-16 text-center text-xl font-black text-red-600 font-mono italic bg-gray-50 border rounded-lg py-1"
          />
        ) : (
          <span className="text-2xl font-black text-red-600 font-mono italic">{val != null && val !== "" ? val : "—"}</span>
        )}
        <span className="text-[9px] font-bold text-gray-300 mt-1 uppercase">
          {subLabel}
        </span>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[100]"
          />

          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none text-left">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="w-full max-w-3xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-white pointer-events-auto max-h-[95vh] overflow-y-auto custom-scrollbar"
            >
              {/* VÙNG IN PDF - ID: transcript-content */}
              <div id="transcript-content" className="bg-white">
                {/* Header */}
                <div className="relative p-8 bg-white border-b border-gray-100 flex flex-col md:flex-row items-center gap-6">
                  {/* Nút đóng được đổi màu để hài hòa với nền trắng */}
                  <button
                    onClick={onClose}
                    className="absolute right-6 top-6 p-2 bg-gray-50 hover:bg-gray-100 rounded-full transition-all text-gray-400 hover:text-gray-600 border border-gray-100"
                  >
                    <X size={20} />
                  </button>

                  <div className="w-24 h-24 rounded-[2rem] bg-red-600 flex items-center justify-center text-3xl font-black text-white shadow-xl shadow-red-100">
                    {student.full_name?.charAt(0)}
                  </div>

                  <div className="text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                      <span className="px-3 py-1 bg-red-500 text-[10px] font-black uppercase rounded-lg tracking-widest text-white">
                        Học viên
                      </span>
                      <span className="text-gray-300 text-sm font-mono italic">
                        #{student.id}
                      </span>
                    </div>
                    <h2 className="text-3xl font-black uppercase italic tracking-tight text-gray-900">
                      {student.full_name}
                    </h2>
                    <p className="text-gray-500 text-sm font-medium flex items-center justify-center md:justify-start gap-2 mt-1">
                      <GraduationCap size={16} className="text-red-500" />
                      {student.class_name} • {student.branch_name}
                    </p>
                  </div>
                </div>

                {/* Body */}
                <div className="p-8 bg-[#F8F9FA] grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-1 space-y-4">
                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100">
                      <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                        <div className="w-1 h-4 bg-red-500 rounded-full" /> Lớp
                        học
                      </h3>
                      <div className="space-y-1">
                        {infoItem(
                          BookOpen,
                          "Khóa học",
                          student.course_name || student.class_name,
                          "text-blue-600"
                        )}
                        {infoItem(
                          Hash,
                          "Mã học viên",
                          student.student_id ? `SV-${String(student.student_id).slice(0, 8)}` : `#${student.id?.slice(0, 8)}`,
                          "text-gray-500"
                        )}
                        {infoItem(
                          Calendar,
                          "Kỳ đánh giá",
                          student.exam_period || "Học kỳ I - 2026",
                          "text-orange-600"
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2 space-y-6">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {scoreBadge("Đầu vào", student.entrance_score, "Entrance", "entrance_score")}
                      {scoreBadge("Giữa kì 1", student.midterm_1, "GK1", "midterm_1")}
                      {scoreBadge("Cuối kì 1", student.final_1, "CK1", "final_1")}
                      {scoreBadge("Giữa kì 2", student.midterm_2, "GK2", "midterm_2")}
                      {scoreBadge("Cuối kì 2", student.final_2, "CK2", "final_2")}
                      {scoreBadge("Đầu ra", student.exit_score, "Exit", "exit_score")}
                    </div>

                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-8 opacity-5">
                        <TrendingUp size={120} className="text-red-600" />
                      </div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1">
                              Kết quả tổng kết
                            </h3>
                            <div className="flex items-center gap-3">
                              <span className="text-4xl font-black text-gray-900 font-mono italic">
                                {student.avg != null ? student.avg : "—"}
                              </span>
                              <span className="px-4 py-1.5 bg-green-100 text-green-700 text-[10px] font-black uppercase rounded-xl border border-green-200">
                                Xếp loại: {getRankText(student.avg)}
                              </span>
                            </div>
                          </div>
                          <Award size={48} className="text-yellow-400" />
                        </div>
                        <div className="pt-6 border-t border-gray-50">
                          <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                            <FileText size={14} /> Nhận xét từ giáo viên
                          </h3>
                          {isEditing ? (
                            <textarea
                              value={form.remarks}
                              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                              className="w-full text-sm text-gray-600 font-medium bg-gray-50 p-4 rounded-2xl border border-dashed border-gray-200 outline-none focus:border-red-200 min-h-[80px]"
                              placeholder="Nhận xét từ giáo viên..."
                            />
                          ) : (
                            <p className="text-sm text-gray-600 leading-relaxed italic font-medium bg-gray-50 p-4 rounded-2xl border border-dashed border-gray-200">
                              "{student.remarks || "Học viên có thái độ học tập tích cực, nắm vững kiến thức cơ bản."}"
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer hành động */}
              <div className="p-6 border-t border-gray-100 flex justify-between flex-wrap gap-3 bg-white">
                <div className="flex gap-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-6 py-3 rounded-xl font-black text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all uppercase tracking-widest"
                      >
                        Hủy
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="px-8 py-3 bg-red-600 text-white rounded-xl font-black text-[11px] flex items-center gap-2 uppercase tracking-widest disabled:opacity-60"
                      >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Lưu
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-6 py-3 rounded-xl font-black text-[11px] text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all uppercase tracking-widest flex items-center gap-2"
                    >
                      <Edit3 size={14} /> Chỉnh sửa
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={onClose}
                    disabled={isExporting}
                    className="px-8 py-3 rounded-xl font-black text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all uppercase tracking-widest disabled:opacity-50"
                  >
                    Đóng
                  </button>
                  <button
                    onClick={handleExportPDF}
                  disabled={isExporting} // Vô hiệu hóa khi đang xử lý
                  className={`px-8 py-3 bg-gray-900 text-white font-black text-[11px] rounded-xl flex items-center gap-2 shadow-lg transition-all active:scale-95 uppercase tracking-widest
                    ${
                      isExporting
                        ? "opacity-80 cursor-not-allowed"
                        : "hover:bg-black shadow-gray-200"
                    }
                  `}
                >
                  {isExporting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Đang xử lý...
                    </>
                  ) : (
                    <>
                      <FileDown size={14} />
                      Xuất bảng điểm (PDF)
                    </>
                  )}
                </button>
              </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default StudentTranscriptModal;
